import { create } from 'zustand';

export interface CableData {
  id: string;
  port_a_id: string;
  port_b_id: string;
  status: string;
  cable_type: string;
  color: string;
}

export type PatchingPhase = 'idle' | 'source_ready' | 'destination_ready' | 'endpoints_ready' | 'ports_matched';

export function getPatchingPhase(state: PatchingState): PatchingPhase {
  const hasLeft = Boolean(state.leftEquipmentId);
  const hasRight = Boolean(state.rightEquipmentId);

  if (!hasLeft && !hasRight) return 'idle';
  if (hasLeft && !hasRight) return 'source_ready';
  if (!hasLeft && hasRight) return 'destination_ready';

  const leftPortCount = (state.selectedLeftPorts || []).length;
  const rightPortCount = (state.selectedRightPorts || []).length;

  if (leftPortCount > 0 && leftPortCount === rightPortCount) {
    return 'ports_matched';
  }
  return 'endpoints_ready';
}

export interface PatchingState {
  isPatchingMode: boolean;
  leftEquipmentId: string | null;
  rightEquipmentId: string | null;
  selectedLeftPorts: string[];
  selectedRightPorts: string[];

  // [D1] Хранение данных о кабелях — нужно для Commit All
  leftCables: CableData[];
  rightCables: CableData[];
  setLeftCables: (cables: CableData[]) => void;
  setRightCables: (cables: CableData[]) => void;

  setPatchingMode: (active: boolean) => void;
  setLeftEquipment: (id: string | number | null) => void;
  setRightEquipment: (id: string | number | null) => void;

  toggleLeftPort: (portId: string) => void;
  toggleRightPort: (portId: string) => void;

  clearLeftSelection: () => void;
  clearRightSelection: () => void;

  patchingTrigger: number;
  triggerPatchingRefresh: () => void;

  // Для bulk sequential selection
  setLeftPorts: (portIds: string[]) => void;
  setRightPorts: (portIds: string[]) => void;

  resetWorkbench: () => void;
}

export const usePatchingStore = create<PatchingState>((set) => ({
  isPatchingMode: false,
  leftEquipmentId: null,
  rightEquipmentId: null,
  selectedLeftPorts: [],
  selectedRightPorts: [],

  // [D1] Инициализация кабельных данных
  leftCables: [],
  rightCables: [],
  setLeftCables: (cables) => set({ leftCables: cables }),
  setRightCables: (cables) => set({ rightCables: cables }),

  setPatchingMode: (active) => set((state) => ({
    isPatchingMode: active,
    leftEquipmentId: active ? state.leftEquipmentId : null,
    rightEquipmentId: active ? state.rightEquipmentId : null,
    selectedLeftPorts: active ? state.selectedLeftPorts : [],
    selectedRightPorts: active ? state.selectedRightPorts : [],
    leftCables: active ? state.leftCables : [],
    rightCables: active ? state.rightCables : [],
  })),

  setLeftEquipment: (id) => set((state) => {
    if (id === null || id === undefined) {
      return { leftEquipmentId: null, selectedLeftPorts: [], leftCables: [] };
    }
    const strId = String(id);
    if (state.rightEquipmentId !== null && String(state.rightEquipmentId) === strId) {
      return state;
    }
    return { leftEquipmentId: strId, selectedLeftPorts: [], leftCables: [] };
  }),

  setRightEquipment: (id) => set((state) => {
    if (id === null || id === undefined) {
      return { rightEquipmentId: null, selectedRightPorts: [], rightCables: [] };
    }
    const strId = String(id);
    if (state.leftEquipmentId !== null && String(state.leftEquipmentId) === strId) {
      return state;
    }
    return { rightEquipmentId: strId, selectedRightPorts: [], rightCables: [] };
  }),

  resetWorkbench: () => set({
    leftEquipmentId: null,
    rightEquipmentId: null,
    selectedLeftPorts: [],
    selectedRightPorts: [],
    leftCables: [],
    rightCables: [],
  }),

  toggleLeftPort: (portId) => set((state) => ({
    selectedLeftPorts: state.selectedLeftPorts.includes(portId)
      ? state.selectedLeftPorts.filter(id => id !== portId)
      : [...state.selectedLeftPorts, portId]
  })),

  toggleRightPort: (portId) => set((state) => ({
    selectedRightPorts: state.selectedRightPorts.includes(portId)
      ? state.selectedRightPorts.filter(id => id !== portId)
      : [...state.selectedRightPorts, portId]
  })),

  clearLeftSelection: () => set({ selectedLeftPorts: [] }),
  clearRightSelection: () => set({ selectedRightPorts: [] }),

  patchingTrigger: 0,
  triggerPatchingRefresh: () => set((state) => ({ patchingTrigger: state.patchingTrigger + 1 })),

  setLeftPorts: (portIds) => set({ selectedLeftPorts: portIds }),
  setRightPorts: (portIds) => set({ selectedRightPorts: portIds }),
}));
